import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import Navbar from "@/components/Navbar";

export default function BookingCreate() {
  const { providerId } = useParams();
  const nav = useNavigate();
  const [provider, setProvider] = useState(null);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    category_id: "", description: "", postcode: "", address: "",
    preferred_date: "", preferred_time: "morning", budget: "", photos: [],
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/providers/${providerId}`).then(({ data }) => setProvider(data));
    api.get("/categories").then(({ data }) => setCats(data));
  }, [providerId]);

  const onFile = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4);
    Promise.all(files.map((f) => new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(f);
    }))).then((urls) => setForm({ ...form, photos: urls }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        provider_id: providerId,
        category_id: form.category_id || (cats[0]?.id ?? ""),
        description: form.description,
        photos: form.photos,
        postcode: form.postcode,
        address: form.address,
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time,
        budget: form.budget ? parseFloat(form.budget) : null,
      };
      const { data } = await api.post("/bookings", payload);
      toast.success("Booking request sent!");
      nav(`/bookings/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create booking");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Toaster position="top-center" />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-display text-3xl font-bold">Request a quote{provider ? ` — ${provider.business_name}` : ""}</h1>
        <form onSubmit={submit} className="mt-6 card space-y-4">
          <div>
            <label className="label">Service</label>
            <select data-testid="booking-category" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="field">
              <option value="">Select service</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Describe the job</label>
            <textarea data-testid="booking-description" required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="field" placeholder="e.g. Rats in the loft, please help." />
          </div>
          <div>
            <label className="label">Add photos (optional)</label>
            <input data-testid="booking-photos" type="file" accept="image/*" multiple onChange={onFile} className="field bg-white" />
            {form.photos.length > 0 && (
              <div className="mt-2 flex gap-2 flex-wrap">
                {form.photos.map((p, i) => <img key={i} src={p} className="w-16 h-16 object-cover rounded-lg" alt="" />)}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Postcode</label>
              <input data-testid="booking-postcode" required value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} className="field" placeholder="L1 8JQ" />
            </div>
            <div>
              <label className="label">Address (optional)</label>
              <input data-testid="booking-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="field" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Preferred date</label>
              <input data-testid="booking-date" type="date" required value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="field" />
            </div>
            <div>
              <label className="label">Preferred time</label>
              <select data-testid="booking-time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className="field">
                <option value="morning">Morning</option>
                <option value="afternoon">Afternoon</option>
                <option value="evening">Evening</option>
                <option value="anytime">Anytime</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label">Budget (£, optional)</label>
            <input data-testid="booking-budget" type="number" min="0" step="1" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="field" />
          </div>
          <button data-testid="booking-submit" disabled={busy} className="btn-accent w-full">{busy ? "Sending…" : "Send request"}</button>
        </form>
      </div>
    </div>
  );
}
